import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, PhoneCall } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { eur } from "@/lib/api";
import ContactModal from "@/components/ContactModal";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count, shippingPreview, paymentEnabled } = useCart();
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);
  const shipping = subtotal === 0 ? 0 : (shippingPreview.price ?? 0);
  const isFreeShipping = shippingPreview.free;

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  const contact = () => {
    setOpen(false);
    setContactOpen(true);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          data-testid="cart-drawer"
          className="w-full sm:max-w-md bg-cream border-l border-stone flex flex-col p-0"
        >
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-stone">
            <SheetTitle className="font-serif text-2xl text-ink text-left">
              Panier {count > 0 && <span className="text-ash text-lg">({count})</span>}
            </SheetTitle>
            <SheetDescription className="sr-only">Vos articles sélectionnés</SheetDescription>
          </SheetHeader>

          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
              <ShoppingBag className="h-12 w-12 text-stone" strokeWidth={1} />
              <p className="text-graphite">Votre panier est vide.</p>
              <button
                data-testid="cart-empty-shop-btn"
                onClick={() => { setOpen(false); navigate("/boutique"); }}
                className="text-xs uppercase tracking-[0.15em] underline underline-offset-4 text-ink"
              >
                Découvrir la boutique
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                {items.map((i) => (
                  <div key={i.product_id} data-testid={`cart-item-${i.slug}`} className="flex gap-4">
                    <img src={i.image} alt={i.name} className="h-24 w-20 object-cover bg-sand rounded-sm" />
                    <div className="flex-1 flex flex-col">
                      <p className="font-sans font-medium text-ink text-sm leading-snug">{i.name}</p>
                      <p className="font-mono text-sm text-graphite mt-1">{eur(i.price)}</p>
                      <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-center border border-stone">
                          <button
                            data-testid={`cart-dec-${i.slug}`}
                            onClick={() => updateQty(i.product_id, i.quantity - 1)}
                            className="px-2 py-1.5 hover:bg-sand"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="px-3 font-mono text-sm">{i.quantity}</span>
                          <button
                            data-testid={`cart-inc-${i.slug}`}
                            onClick={() => updateQty(i.product_id, i.quantity + 1)}
                            className="px-2 py-1.5 hover:bg-sand"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <button
                          data-testid={`cart-remove-${i.slug}`}
                          onClick={() => removeItem(i.product_id)}
                          className="text-ash hover:text-destructive transition-colors"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <SheetFooter className="border-t border-stone px-6 py-5 flex-col gap-4 sm:flex-col sm:space-x-0">
                <div className="w-full space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-graphite">Sous-total</span>
                    <span className="font-mono text-ink" data-testid="cart-subtotal">{eur(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-graphite">Livraison{shippingPreview.label && <span className="text-ash"> · {shippingPreview.label}</span>}</span>
                    <span className="font-mono text-ink">{isFreeShipping ? "Offerte" : (shipping > 0 ? eur(shipping) : "—")}</span>
                  </div>
                  {!isFreeShipping && subtotal > 0 && shippingPreview.price !== null && (
                    <p className="text-xs text-ash">Tarif estimé pour la France · choix du transporteur au paiement.</p>
                  )}
                </div>
                {paymentEnabled ? (
                  <button
                    data-testid="cart-checkout-btn"
                    onClick={checkout}
                    className="w-full bg-ink text-cream py-4 uppercase tracking-[0.15em] text-xs hover:bg-[#2C2B29] transition-colors"
                  >
                    Commander · {eur(subtotal + shipping)}
                  </button>
                ) : (
                  <button
                    data-testid="cart-contact-btn"
                    onClick={contact}
                    className="w-full bg-ink text-cream py-4 uppercase tracking-[0.15em] text-xs hover:bg-[#2C2B29] transition-colors flex items-center justify-center gap-2"
                  >
                    <PhoneCall className="h-4 w-4" /> Contacter
                  </button>
                )}
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      <ContactModal open={contactOpen} onOpenChange={setContactOpen} items={items} subtotal={subtotal} />
    </>
  );
};
